import { ConvertibleEntity } from 'types/ConvertibleEntity'
import ApiBase from './Api'
import { getStoredLanguage, resolveLanguage } from '../i18n/languagePreference'
import { getInvestorAmountInCash, getInvestorAmountInPercentage } from './utils'
import { ConvertibleAction } from 'types/ConvertibleAction'
import { ConvertiblePatchBody, ConvertiblePostBody } from '@finance-lab/utils/types'

const setupDerivedFields = (convertible: ConvertibleEntity) => {
    const loanAmount = convertible.convertibleActions.reduce((acc: number, action: ConvertibleAction) => {
        if (action.actionType === 'new_loan' && action.loanAmount !== null) {
            acc += action.loanAmount
        }
        return acc
    }, 0)

    return {
        ...convertible,
        investors: convertible.investors.map((investor) => ({
            ...investor,
            amountInCash: getInvestorAmountInCash(investor, loanAmount),
            amountInPercentage: getInvestorAmountInPercentage(investor, loanAmount),
        })),
    }
}

const CONVERTIBLES_API_PATHS = {
    CONVERTIBLES: '/convertibles',
    CONVERTIBLE: (id: string) => `/convertibles/${id}`,
}

export type ConvertibleInput = {
    shareId: string
}

class ConvertiblesApi extends ApiBase {
    async findAll(): Promise<ConvertibleEntity[]> {
        const response: ConvertibleEntity[] = await this._getRequest(CONVERTIBLES_API_PATHS.CONVERTIBLES)

        return response
    }

    async findByConvertibleId(convertibleId: string, enrichConvertible = true): Promise<ConvertibleEntity | null> {
        if (!convertibleId) {
            return null
        }
        const preferredLanguage = resolveLanguage(getStoredLanguage())
        const fetchedConvertible: ConvertibleEntity = await this._getRequest(
            CONVERTIBLES_API_PATHS.CONVERTIBLE(convertibleId),
            {
                queryParameters: { preferredLanguage },
            },
        )

        if (enrichConvertible) {
            return setupDerivedFields(fetchedConvertible)
        }

        return fetchedConvertible
    }

    async findByCompanyId(companyId: string): Promise<ConvertibleEntity[]> {
        if (!companyId) {
            return []
        }
        const fetchedConvertibles: ConvertibleEntity[] = await this._getRequest(CONVERTIBLES_API_PATHS.CONVERTIBLES, {
            queryParameters: { companyId },
        })

        return fetchedConvertibles
    }

    async create(convertible: ConvertiblePostBody): Promise<ConvertibleEntity> {
        const response: ConvertibleEntity = await this._postRequest(CONVERTIBLES_API_PATHS.CONVERTIBLES, convertible)

        return response
    }

    async update(convertibleToUpdate: ConvertiblePatchBody): Promise<ConvertibleEntity> {
        const response = (await this._patchRequest(
            CONVERTIBLES_API_PATHS.CONVERTIBLES + '/' + convertibleToUpdate.id,
            convertibleToUpdate,
        )) as ConvertibleEntity

        return response
    }

    async delete(id: string): Promise<{ message: string }> {
        const response = (await this._deleteRequest(CONVERTIBLES_API_PATHS.CONVERTIBLES + '/' + id)) as {
            message: string
        }

        return response
    }
}

export default ConvertiblesApi
